import React, { useState, useEffect, useCallback } from 'react';
import { MessagesSquare, Loader2, ChevronDown, ChevronUp, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatHistoryTab = ({ token }) => {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [messages, setMessages] = useState({});

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchConvos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/chatbot/conversations`, { headers });
      if (res.ok) setConvos(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  const toggleConvo = async (sid) => {
    if (expanded === sid) { setExpanded(null); return; }
    setExpanded(sid);
    if (!messages[sid]) {
      const res = await fetch(`${API_URL}/api/admin/chatbot/conversations/${sid}`, { headers });
      const data = res.ok ? await res.json() : [];
      setMessages(prev => ({ ...prev, [sid]: data }));
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-4 md:space-y-6" data-testid="chat-history-tab">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
          <MessagesSquare className="w-5 h-5 text-indigo-400" />
        </div>
        <div>
          <h2 className="text-white font-bold text-base md:text-lg">Conversations AqmChatBot</h2>
          <p className="text-gray-400 text-xs">Ce que demandent tes visiteurs au chatbot du site</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin mx-auto" /></div>
      ) : convos.length === 0 ? (
        <div className="text-center py-10 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <MessagesSquare className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">Aucune conversation pour l'instant</p>
        </div>
      ) : (
        <div className="space-y-2">
          {convos.map((c) => (
            <div key={c.session_id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleConvo(c.session_id)}
                data-testid={`chat-convo-${c.session_id.slice(0, 8)}`}
                className="w-full flex items-center justify-between gap-3 p-3.5 hover:bg-white/[0.02] transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-indigo-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{c.first_message}</p>
                    <p className="text-gray-500 text-[11px]">{fmtDate(c.last_ts)} • {c.count} messages • {c.session_id.slice(0, 8)}</p>
                  </div>
                </div>
                {expanded === c.session_id ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />}
              </button>
              {expanded === c.session_id && (
                <div className="border-t border-zinc-800 p-3.5 space-y-2 max-h-96 overflow-y-auto bg-zinc-950/50">
                  {!messages[c.session_id] ? (
                    <Loader2 className="w-4 h-4 text-indigo-500 animate-spin mx-auto" />
                  ) : messages[c.session_id].map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs whitespace-pre-wrap ${
                        m.role === 'user' ? 'bg-cyan-600/80 text-white' : 'bg-zinc-800 text-gray-300'
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatHistoryTab;
